<script context="module" lang="ts">
    import { writable } from "svelte/store";

    let lastCancel = writable<undefined | (() => void)>(undefined);
</script>

<script lang="ts">
    import type { CustomSetting } from "..";
    import { type Command, Shortcut } from "../..";
    import { createStringFilter } from "../createFilter";
    import { onMount } from "svelte";
    import { linkHandling } from "../link";

    let elem: HTMLElement;

    export let setting: CustomSetting<string>;

    let value = setting.value;
    const command = setting.data as Command;

    let edit = false;

    let timeout: any;

    function click() {
        if (timeout) {
            startEdit();
            return;
        }

        timeout = setTimeout(() => timeout = null, 200);
    }

    function startEdit() {
        if (!command.canRebind) return;
        if (edit) return;

        $lastCancel?.();
        edit = true;
        $lastCancel = cancel;

        clearTimeout(timeout);
        timeout = null;

        setTimeout(() => elem.focus());
    }


    function done(e?: Event) {
        if (!edit) return;

        setting.value = value;
        setting = setting;
        $lastCancel = undefined;
        edit = false;
        e?.stopPropagation();
    }

    function cancel(e?: Event) {
        if (!edit) return;

        value = setting.value;
        $lastCancel = undefined;
        edit = false;
        e?.stopPropagation();
    }

    function onKeyDown(e: KeyboardEvent) {
        if (!edit) return;

        let key = e.key.toLowerCase();

        if (key === "Escape") {
            cancel();
            return;
        }

        if (key === "Enter") {
            setting.value = value;
            done();
            return;
        }

        if (key.startsWith("arrow")) key = key.slice(5);

        if (["control", "alt", "altgraph", "meta", "shift"].find((k) => k === key)) {
            return;
        }

        let shortcut = "";
        shortcut += e.ctrlKey ? "ctrl+" : "";
        shortcut += e.altKey ? "alt+" : "";
        shortcut += e.shiftKey ? "shift+" : "";
        shortcut += key;

        value = shortcut;

        e.preventDefault();
        e.stopPropagation();
    }

    function focus() {
        elem.scrollIntoView({
            block: "start",
            behavior: "smooth",
        });
        elem.setAttribute("data-search", "included");
    }

    onMount(() => {
        linkHandling.settingsCallbacks[setting.id] = focus;
        return () => linkHandling.settingsCallbacks[setting.id] = undefined;
    });

    $: filter = createStringFilter(setting.name + ":" + setting.value);
</script>

<!----------------------------------------------------------------------------->

<div bind:this={elem} class="command" on:click={click} use:filter class:editing={edit} role="button" tabindex="0" on:keydown={undefined}>
  <span class="name">{setting.name}</span>

  <div class="shortcut">
    {#key setting}
      <Shortcut hotkey={setting.value} />
    {/key}
  </div>

  {#if command.canRebind}
    <button class="input-small" on:click={startEdit} disabled={edit}>
      Edit
    </button>
  {:else}
    <div style="opacity: 0" class="input-button input-small">
      Edit
    </div>
  {/if}

  {#if edit}
    <div class="edit-command">
      <input class="input-small" type="text" bind:value={value} bind:this={elem} on:keydown={onKeyDown} />

      <button class="input-small" on:click={done}>Done</button>
      <button class="input-small" on:click={cancel}>Cancel</button>
    </div>
  {/if}
</div>


<!----------------------------------------------------------------------------->

<style>
    .command {
        all: unset;
        display: grid;
        grid-template-columns: 1fr max-content max-content;
        grid-column: 1 / -1;
        grid-template-rows: 2rem;
        align-items: center;

        border-left: solid 3px transparent;
        padding-inline: 1rem 0.5rem;
    }

    .shortcut {
        display: flex;
        align-items: center;
        padding-inline: 1rem;
        justify-self: right;
    }

    .edit-command {
        grid-column: 1 / -1;
        display: flex;
        gap: 0.5rem;
        padding: 0.5rem;
    }


    /* filter */
    :global(*:has(.editing)) .command:not(.editing), .command:global(:where([data-search="excluded"])) {
        opacity: 0.5;
    }

    .editing, .command:global(:where([data-search="included"])) {
        background: var(--g100);
        border-color: var(--gacc200);
        opacity: 1;
    }

    :global(*:has(.editing)) .command:not(.editing):global(:where([data-search="included"])) {
        opacity: 0.5;
    }
</style>
